import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { Box, Modal as ModalContainer } from '@mui/material';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useTranslation } from 'react-i18next';
import {
    UploadDocResponse,
    Document,
    DocumentFile
} from '@/services/PaymentsServiceV2';
import { useEffect, useState, useMemo } from 'react';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG,
    DangerButtonLG
} from '@/components/shared/Button';
import {
    SMBold,
    SMMedium,
    TextMDSemiBold,
    TextSMRegular,
    Title
} from '@/typography/Typography';
import { theme } from '@/lib/theme';
import { InputText } from '@/components/shared/InputText';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { Dropdown } from '@/components/shared/Dropdown';
import { UploadArea } from './UploadArea';
import { MarkingDialog } from './MarkingDialog';
import { ConfirmationOverlay } from '@/pages/company/disable/ConfirmationOverlay';
import { SUPPORTED_DOCUMENT_LANGUAGES } from '@/utils/constant';
import denmarkFlag from '@/icons/flags/denmark.svg';
import ukFlag from '@/icons/flags/uk.svg';

interface LanguageDocumentState {
    markingDocument?: UploadDocResponse;
    isMarkingDone: boolean;
    signatureData: {
        signatureX: number;
        signatureY: number;
        signatureWidth: number;
        signatureHeight: number;
        signaturePageNumber: number;
    } | null;
    openMarkingDialog: boolean;
}

interface UploadNewDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    document?: Document;
}

export const UploadNewDocumentDialog = ({
    open,
    onClose,
    document
}: UploadNewDocumentDialogProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [requiresNewSignature, setRequiresNewSignature] =
        useState('required');
    const [documentStatus, setDocumentStatus] = useState('active');

    // Dynamic language state management
    const [languageStates, setLanguageStates] = useState<
        Record<string, LanguageDocumentState>
    >(() => {
        const initialStates: Record<string, LanguageDocumentState> = {};
        SUPPORTED_DOCUMENT_LANGUAGES.forEach((lang) => {
            initialStates[lang.code] = {
                markingDocument: undefined,
                isMarkingDone: false,
                signatureData: null,
                openMarkingDialog: false
            };
        });
        return initialStates;
    });

    // Get flag images mapping
    const flagImages: Record<string, string> = {
        da: denmarkFlag,
        en: ukFlag
        // Add more flag mappings as needed
    };

    const isEditMode = !!document;

    const { deleteDocument, createDocument, updateDocument } =
        usePaymentsMutations();

    // Helper function to update language state
    const updateLanguageState = (
        langCode: string,
        updates: Partial<LanguageDocumentState>
    ) => {
        setLanguageStates((prev) => ({
            ...prev,
            [langCode]: {
                ...prev[langCode],
                ...updates
            }
        }));
    };

    // Check if at least one language has marking done
    const hasAnyMarkingDone = useMemo(() => {
        return Object.values(languageStates).some(
            (state) => state.isMarkingDone
        );
    }, [languageStates]);

    const handleClose = () => {
        setName('');
        setShowDeleteConfirmation(false);
        setRequiresNewSignature('required');
        setDocumentStatus('active');

        // Reset all language states
        const resetStates: Record<string, LanguageDocumentState> = {};
        SUPPORTED_DOCUMENT_LANGUAGES.forEach((lang) => {
            resetStates[lang.code] = {
                markingDocument: undefined,
                isMarkingDone: false,
                signatureData: null,
                openMarkingDialog: false
            };
        });
        setLanguageStates(resetStates);

        onClose();
    };

    useHandleResponse({
        error: deleteDocument.error,
        isSuccess: deleteDocument.isSuccess,
        successAlertMessage: t('documents.document_deleted_successfully'),
        isSuccessCallBack: () => {
            setShowDeleteConfirmation(false);
            handleClose();
        }
    });

    useHandleResponse({
        error: createDocument.error,
        isSuccess: createDocument.isSuccess,
        successAlertMessage: t('documents.document_created_successfully'),
        isSuccessCallBack: () => {
            handleClose();
        }
    });

    useHandleResponse({
        error: updateDocument.error,
        isSuccess: updateDocument.isSuccess,
        successAlertMessage: t('documents.document_updated_successfully'),
        isSuccessCallBack: () => {
            handleClose();
        }
    });

    // Initialize form with document data when editing
    useEffect(() => {
        if (document) {
            setName(document?.files?.[0]?.name || '');
            setDocumentStatus(
                document.status === 'Active' ? 'active' : 'inactive'
            );
            setRequiresNewSignature(
                document.requiresReSign ? 'required' : 'not_required'
            );

            // Initialize signature data from existing document files for all languages
            const newLanguageStates: Record<string, LanguageDocumentState> = {
                ...languageStates
            };

            SUPPORTED_DOCUMENT_LANGUAGES.forEach((lang) => {
                const fileForLang = document.files?.find(
                    (file) => file.language === lang.code
                );

                if (fileForLang) {
                    newLanguageStates[lang.code] = {
                        markingDocument: undefined,
                        isMarkingDone: true,
                        signatureData: {
                            signatureX: fileForLang.signatureX,
                            signatureY: fileForLang.signatureY,
                            signatureWidth: fileForLang.signatureWidth,
                            signatureHeight: fileForLang.signatureHeight,
                            signaturePageNumber: fileForLang.signaturePageNumber
                        },
                        openMarkingDialog: false
                    };
                }
            });

            setLanguageStates(newLanguageStates);
        } else {
            setName('');
        }
    }, [document]);

    const handleCreate = () => {
        if (isEditMode && document?.documentId) {
            // In edit mode, include existing document files with updated status and requiresReSign
            updateDocument.mutate({
                documentId: document.documentId,
                data: {
                    requiresNewSignatureFromAll:
                        requiresNewSignature === 'required',
                    status: documentStatus === 'active' ? 'Active' : 'Inactive',
                    documentFiles: document.files || []
                }
            });
        } else {
            // In create mode, build document files dynamically from all languages
            const documentFiles: DocumentFile[] = [];

            SUPPORTED_DOCUMENT_LANGUAGES.forEach((lang) => {
                const langState = languageStates[lang.code];

                if (
                    langState.markingDocument &&
                    langState.isMarkingDone &&
                    langState.signatureData
                ) {
                    documentFiles.push({
                        name: name,
                        url: langState.markingDocument.pdfUrl,
                        signatureX: langState.signatureData.signatureX,
                        signatureY: langState.signatureData.signatureY,
                        signatureWidth: langState.signatureData.signatureWidth,
                        signatureHeight:
                            langState.signatureData.signatureHeight,
                        signaturePageNumber:
                            langState.signatureData.signaturePageNumber,
                        language: lang.code
                    });
                }
            });

            if (documentFiles.length > 0) {
                createDocument.mutate({
                    requiresNewSignatureFromAll:
                        requiresNewSignature === 'required',
                    status: documentStatus === 'active' ? 'Active' : 'Inactive',
                    documentFiles
                });
            }
        }
    };

    return (
        <>
            <ModalContainer open={open} onClose={handleClose}>
                <ConfirmationBox style={{ width: '450px' }}>
                    <ConnectHeader
                        // logo={getLogoForPOSProvider()}
                        title={
                            isEditMode
                                ? t('documents.edit_document')
                                : t('documents.upload_new_document')
                        }
                        description={t(
                            'documents.employee_signing_requirement'
                        )}
                    />

                    <Box alignItems={'flex-start'} mt={2} mb={1}>
                        <InputText
                            label={t('documents.document_name_label')}
                            value={name}
                            disabled={isEditMode}
                            onChange={(e) => setName(e.target.value)}
                        ></InputText>

                        <SMMedium mt={2}>
                            {t('documents.upload_pdf_requirement')}
                        </SMMedium>

                        {/* Dynamic language upload areas */}
                        {SUPPORTED_DOCUMENT_LANGUAGES.map((lang) => {
                            const langState = languageStates[lang.code];
                            const shouldShow =
                                !isEditMode ||
                                !document?.files ||
                                document.files.length === 0 ||
                                document.files.some(
                                    (file) => file.language === lang.code
                                );

                            if (!shouldShow) return null;

                            return (
                                <UploadArea
                                    key={lang.code}
                                    isChangeDisabled={isEditMode}
                                    isMarkingDone={
                                        langState.isMarkingDone && !isEditMode
                                    }
                                    signaturePageNumber={
                                        langState.signatureData
                                            ?.signaturePageNumber
                                    }
                                    label={`Upload ${lang.name} document`}
                                    successLabel={`${lang.name} document uploaded`}
                                    image={flagImages[lang.code]}
                                    description="Define signature area"
                                    handleSign={(doc) => {
                                        updateLanguageState(lang.code, {
                                            markingDocument: doc,
                                            openMarkingDialog: true
                                        });
                                    }}
                                    onHandleDiscard={() => {
                                        updateLanguageState(lang.code, {
                                            isMarkingDone: false,
                                            markingDocument: undefined,
                                            signatureData: null
                                        });
                                    }}
                                />
                            );
                        })}

                        <Title mt={2}>{t('documents.require_resign')}</Title>
                        <Dropdown
                            // label={''}
                            choices={[
                                {
                                    label: t('documents.resign_required'),
                                    value: 'required'
                                },
                                {
                                    label: t('documents.resign_not_required'),
                                    value: 'not_required'
                                }
                            ]}
                            isDisabled={isEditMode}
                            value={requiresNewSignature}
                            allowClear={false}
                            onChange={(value) => {
                                if (value != null) {
                                    setRequiresNewSignature(value);
                                }
                            }}
                        />
                        <Title mt={2}>
                            {t('documents.document_status_label')}
                        </Title>
                        <Dropdown
                            allowClear={false}
                            label={''}
                            choices={[
                                {
                                    label: t(
                                        'documents.status_active',
                                        'Active'
                                    ),
                                    value: 'active'
                                },
                                {
                                    label: t(
                                        'documents.status_inactive',
                                        'Inactive'
                                    ),
                                    value: 'inactive'
                                }
                            ]}
                            value={documentStatus}
                            onChange={(value) => {
                                setDocumentStatus(value);
                            }}
                        />
                    </Box>

                    <Box
                        width={'100%'}
                        sx={{
                            display: 'flex',
                            justifyContent: isEditMode
                                ? 'space-between'
                                : 'flex-end',
                            mt: 4,
                            mb: 2
                        }}
                        gap={1.5}
                    >
                        <Box width={'100%'} display={'flex'} gap={1.5}>
                            <OutlinedSecondaryButtonLG
                                sx={{ width: isEditMode ? '33%' : '50%' }}
                                onClick={handleClose}
                            >
                                <SMBold textTransform={'none'}>
                                    {t('cancel')}
                                </SMBold>
                            </OutlinedSecondaryButtonLG>

                            {isEditMode && (
                                <DangerButtonLG
                                    onClick={() =>
                                        setShowDeleteConfirmation(true)
                                    }
                                    sx={{ width: '33%' }}
                                >
                                    <SMBold textTransform={'none'}>
                                        {t('delete')}
                                    </SMBold>
                                </DangerButtonLG>
                            )}

                            <PrimaryButtonLG
                                sx={{ width: isEditMode ? '33%' : '50%' }}
                                onClick={handleCreate}
                                disabled={
                                    isEditMode
                                        ? updateDocument.isPending
                                        : !name ||
                                          !hasAnyMarkingDone ||
                                          createDocument.isPending
                                }
                            >
                                <SMBold textTransform={'none'}>
                                    {isEditMode ? t('save') : t('create')}
                                </SMBold>
                            </PrimaryButtonLG>
                        </Box>
                    </Box>
                </ConfirmationBox>
            </ModalContainer>

            {/* Dynamic marking dialogs for each language */}
            {SUPPORTED_DOCUMENT_LANGUAGES.map((lang) => {
                const langState = languageStates[lang.code];
                return (
                    <MarkingDialog
                        key={lang.code}
                        markingDocument={langState.markingDocument}
                        open={langState.openMarkingDialog}
                        onClose={() =>
                            updateLanguageState(lang.code, {
                                openMarkingDialog: false
                            })
                        }
                        handleMarkingDone={(signatureData) => {
                            // console.log('Signature Data:', signatureData);
                            updateLanguageState(lang.code, {
                                signatureData,
                                isMarkingDone: true,
                                openMarkingDialog: false
                            });
                        }}
                    />
                );
            })}

            <ConfirmationOverlay
                danger
                onCancel={() => setShowDeleteConfirmation(false)}
                onContinue={() => {
                    if (document?.documentId) {
                        deleteDocument.mutate(document.documentId);
                    }
                }}
                loading={deleteDocument.isPending}
                open={showDeleteConfirmation}
                p1={
                    <>
                        <TextMDSemiBold mb={1} className="text-center">
                            {t('documents.delete_document_title')}
                        </TextMDSemiBold>
                        <TextMDSemiBold className="text-center">
                            {document?.files?.[0]?.name}
                        </TextMDSemiBold>
                    </>
                }
                continueText={t('delete')}
                displayAreYouSureText={false}
                p2={
                    <TextSMRegular
                        mb={2}
                        color={theme.palette.text.muted}
                        className="text-center"
                    >
                        {t('documents.delete_document_confirmation')}
                    </TextSMRegular>
                }
            />
        </>
    );
};
